"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useAgentApiCreateConversation = useAgentApiCreateConversation;
// apis/agent-api/hooks/useCreateConversation.ts
const recoil_1 = require("recoil");
const shared_1 = require("@heartcheck/shared/");
const state_1 = require("../state");
const createConversationQuery = new shared_1.GraphqlQuery(
/* GraphQL */ `
    mutation CreateConversation($config: NewConversation!) {
      createConversation(config: $config) {
        id
      }
    }
  `, 
// ✅ Use Cognito User Pools (ID token) via the proxy
{ forwardAuthMode: "userpools" });
function useAgentApiCreateConversation(validAgentIds) {
    const setConversationsValue = (0, recoil_1.useSetRecoilState)(state_1.Conversations);
    const setPartialResults = (0, recoil_1.useSetRecoilState)(state_1.ConversationPartialResults);
    return async (agentId) => {
        if (!agentId || !validAgentIds.includes(agentId)) {
            const errorMessage = `❌ Invalid agentId: ${agentId} not in validAgentIds`;
            // eslint-disable-next-line no-console
            console.error(errorMessage);
            throw new Error(errorMessage);
        }
        try {
            const response = await createConversationQuery.invoke({
                config: { agent: agentId },
            });
            const newChatId = response?.createConversation?.id;
            if (!newChatId) {
                throw new Error("❌ Missing conversation ID in response");
            }
            // eslint-disable-next-line no-console
            console.log("✅ Conversation created:", newChatId);
            // Optimistically insert/ensure the conversation exists in local state for immediate UI binding.
            setConversationsValue((prev) => {
                const prevRecord = prev.value ?? state_1.ObjRecord.empty();
                // Avoid duplicates if the conversation already exists
                if (prevRecord.get(newChatId)) {
                    return state_1.Loadable.loaded(prevRecord);
                }
                const placeholder = {
                    id: newChatId,
                    agent: agentId,
                    events: [],
                    timestamp: "", // server will populate on next fetch
                    origin: "manual", // ✅ align with list filter (manual-only)
                };
                const updated = state_1.ObjRecord.of([
                    ...prevRecord.items(),
                    placeholder,
                ]);
                return state_1.Loadable.loaded(updated);
            });
            // ✅ Initialize conversation-scoped metadata
            const initialAgentMeta = {
                agentStartResponding: null,
                agentStopResponding: null,
                agentPartialMessage: "",
                updatedAt: new Date().toISOString(),
            };
            setPartialResults((prev) => {
                const existing = prev[newChatId];
                const existingByAgent = existing?.byAgent ?? {};
                const nextConv = {
                    activeAgentId: existing?.activeAgentId ?? agentId,
                    byAgent: {
                        ...existingByAgent,
                        [agentId]: {
                            ...(existingByAgent[agentId] ?? {}),
                            ...initialAgentMeta,
                        },
                    },
                };
                return { ...prev, [newChatId]: nextConv };
            });
            return newChatId;
        }
        catch (err) {
            // eslint-disable-next-line no-console
            console.error("❌ Failed to create conversation:", err);
            throw new Error(err instanceof Error ? err.message : "Conversation creation failed");
        }
    };
}
